import _ from 'lodash';

import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';
import { fToNow, fDate } from 'src/utils/format-time';
import { TenantType } from 'src/types/enums';
import { IOrgCandidateListItem } from 'src/services/candidates/candidates.types';

import StarRating from 'src/components/rating/star-rating';
import AvailabilityStatusLabel from 'src/components/candidates/availability-status-label';
import RestrictedInfoLabel from 'src/components/restricted-info-label/restricted-info-label';
import { OrgCandidateTypeIcon } from 'src/components/candidates/candidate-type.icon';

import { ColumnDef } from 'src/components/data-list/data-list.types';

// ----------------------------------------------------------------------

type Translate = (key: string, options?: any) => string;

interface BuildCandidateColumnsArgs {
  t: Translate;
  tenantType?: TenantType;
  onRatingUpdate: (candidateId: string, rating: number) => void;
}

const dash = <Typography variant="body2" color="textSecondary">--</Typography>;

/** Contact cell: restricted label for non-privileged rows, a mailto/tel link when present, else a dash. */
function renderContact(row: IOrgCandidateListItem, kind: 'email' | 'phone', t: Translate) {
  if (!row.privileged_access) {
    return <RestrictedInfoLabel title={t('common.via_recruiter')} tooltip={t('candidates.privileged_access_tootip')} />;
  }
  if (kind === 'email') {
    if (!row.email) return dash;
    return <Link variant="body2" underline="hover" color="InfoText" href={`mailto:${row.email}`}>{row.email}</Link>;
  }
  if (!row.phone_number) return dash;
  return <Link variant="body2" underline="hover" color="InfoText" href={`tel:${row.phone_number}`}>{row.country_code} {row.phone_number}</Link>;
}

const formatSalary = (range: IOrgCandidateListItem['salary_range'], t: Translate): string => {
  if (!range) return '';
  const fmt = (n: number) => (n >= 1000 ? `${Math.round(n / 100) / 10}k` : `${n}`);
  const period = t(`enums.period_type.${range.period?.toLowerCase?.() ?? range.period}`, { defaultValue: '' });
  return `$${fmt(range.min_amount)}–$${fmt(range.max_amount)}${period ? ` / ${period}` : ''}`;
};

/**
 * Candidate list column registry (JT1-1527/1528). A factory so interactive columns (rating) close
 * over their callbacks. Contact fields honour the row's privileged_access (the lean endpoint already
 * nulls them for non-privileged rows; the restricted label mirrors the old list). Availability is
 * recruiter-only, matching the previous behaviour.
 */
export function buildCandidateColumns({ t, tenantType, onRatingUpdate }: BuildCandidateColumnsArgs): ColumnDef<IOrgCandidateListItem>[] {
  const columns: ColumnDef<IOrgCandidateListItem>[] = [
    {
      key: 'name',
      label: t('candidates.table.name'),
      fixed: true,
      sortable: true,
      searchable: true,
      minWidth: 220,
      render: (row) => (
        <Stack direction="row" alignItems="flex-start" gap={0.5}>
          <Box sx={{ mt: 0.5 }}>
            <OrgCandidateTypeIcon type={row.type} />
          </Box>
          <Stack direction="column">
            {/* Name still links to the profile until the context-preserving overlay (item 13/JT1-1529) replaces it. */}
            <Link
              component={RouterLink}
              href={paths.dashboard.candidates.detail(row.id)}
              color="inherit"
              noWrap
              sx={{ textTransform: 'capitalize' }}
            >
              <Typography variant="subtitle2">
                {_.capitalize(`${row.first_name ?? ''} ${row.last_name ?? ''}`.trim()) || '--'}
              </Typography>
            </Link>
            <Typography variant="caption" color="text.secondary">
              {!_.isEmpty(row.position_title) ? row.position_title : t('candidates.table.no_position_title')}
            </Typography>
          </Stack>
        </Stack>
      ),
    },
    {
      key: 'email',
      label: t('candidates.table.email'),
      defaultVisible: true,
      searchable: true,
      minWidth: 180,
      render: (row) => renderContact(row, 'email', t),
    },
    {
      key: 'phone',
      label: t('candidates.table.phone'),
      defaultVisible: true,
      minWidth: 150,
      render: (row) => renderContact(row, 'phone', t),
    },
    {
      key: 'location',
      label: t('candidates.table.location', { defaultValue: 'Location' }),
      defaultVisible: true,
      sortable: true,
      searchable: true,
      render: (row) => (row.location ? <Typography variant="body2" color="text.secondary">{row.location}</Typography> : dash),
    },
    {
      key: 'salary_range',
      label: t('candidates.table.salary_range', { defaultValue: 'Salary range' }),
      defaultVisible: true,
      render: (row) => (row.salary_range ? <Typography variant="body2" color="text.secondary">{formatSalary(row.salary_range, t)}</Typography> : dash),
    },
    {
      key: 'notice_period',
      label: t('candidates.table.notice_period', { defaultValue: 'Notice period' }),
      defaultVisible: false,
      render: (row) =>
        row.notice_period
          ? <Typography variant="body2" color="text.secondary">{t(`enums.notice_period_type.${row.notice_period}`, { defaultValue: row.notice_period })}</Typography>
          : dash,
    },
    {
      key: 'rating',
      label: t('candidates.table.rating'),
      defaultVisible: true,
      sortable: true,
      render: (row) => (
        <StarRating rating={row.rating} onSelectExcitementRating={(newRating) => onRatingUpdate(row.id, newRating)} />
      ),
    },
    {
      key: 'last_activity_at',
      label: t('candidates.table.last_updated', { defaultValue: 'Last updated' }),
      defaultVisible: true,
      sortable: true,
      render: (row) =>
        row.last_activity_at ? (
          <Tooltip title={fDate(row.last_activity_at)}>
            <Typography variant="body2" color="text.secondary">{fToNow(row.last_activity_at)}</Typography>
          </Tooltip>
        ) : dash,
    },
    {
      key: 'applications',
      label: t('candidates.table.job_applications_count'),
      defaultVisible: true,
      render: (row) =>
        row.job_applications_count ? (
          <Link component={RouterLink} href={paths.dashboard.candidates.detail(row.id, 'jobs')} noWrap color="inherit">
            <Typography variant="subtitle2" color="primary">
              {t('jobs.table.applicant_count', { count: row.job_applications_count })}
            </Typography>
          </Link>
        ) : (
          <Typography variant="body2" color="text.secondary">{t('candidates.table.no_applications')}</Typography>
        ),
    },
  ];

  // Availability is recruiter-only, as in the previous list.
  if (tenantType === TenantType.Recruiter) {
    columns.splice(3, 0, {
      key: 'availability',
      label: t('candidates.table.availability'),
      defaultVisible: true,
      render: (row) =>
        row.availability_status ? <AvailabilityStatusLabel status={row.availability_status} /> : dash,
    });
  }

  return columns;
}
